import { HttpStatusCode } from "axios";
import { RecallLineItemStatusSmartEnum } from "../../../enums/Domain/RecallLineItemStatusSmartEnum";
import { httpPost } from "../../../modules/Backend";
import { getFoundStatusFromQuantity } from "../layout/SidePanel/layouts/SidePanelRecallPickList";
import { IPicklistItem } from "../List/RecallsPickList";
import ConfirmModal, { ConfirmType } from "../Modal/ConfirmModal";
import { modalSignal } from "../Modal/ModalContainer";
import React from "react";
import CancelPicklistModal from "../Modal/CancelPicklistModal";
import { useNavigate } from "react-router";
import { EntityNames } from "../../../enums/EntityNames";

interface UsePickListActionsProps {
    recallId: string | undefined;
    items: IPicklistItem[];
}

export default function useRecallPickListActions({ recallId, items }: UsePickListActionsProps) {

    const navigate = useNavigate();

    function goToRecall() {
        navigate(`/view/${EntityNames.recalls}/${recallId}`, { replace: true });
    }

    const save = async (itemsToSave?: IPicklistItem[]) => {
        // Use provided items or fall back to items from props (for backward compatibility)
        const itemsToUse = itemsToSave ?? items;
        const payload = {
            recallId: recallId,
            items: itemsToUse.map(x => ({
                id: x.id,
                quantity: x.quantity,
                foundStatus: getFoundStatusFromQuantity(x.quantity),
                status: x.status,
                notes: x.notes,
                intent: x.intentDisplayValue
            }))
        };
        await httpPost(`api/${EntityNames.recalls}/savePicklist`, payload);
    };

    const handleCancel = async (notes?: string) => {
        const payload = {
            recallId: recallId,
            cancellationNotes: notes ?? null
        };
        const result = await httpPost(`api/${EntityNames.recalls}/cancelPicklist`, payload);
        if (result.status === HttpStatusCode.Ok) {
            goToRecall();
        }
    };

    const submit = (itemsToSubmit?: IPicklistItem[]) => {
        // Use provided items or fall back to items from props (for backward compatibility)
        const itemsToUse = itemsToSubmit ?? items;
        const shouldCancel = itemsToUse.every((x: IPicklistItem) =>
            x.status.value === RecallLineItemStatusSmartEnum.Cancelled.value
        );

        const handle = async (cancellationNotes: string | undefined) => {
            if (shouldCancel) {
                await handleCancel(cancellationNotes);
            } else {
                const payload = {
                    recallId: recallId,
                    items: itemsToUse.map(x => ({
                        id: x.id,
                        quantity: x.quantity,
                        foundStatus: getFoundStatusFromQuantity(x.quantity),
                        status: x.status,
                        notes: x.notes,
                        intent: x.intentDisplayValue
                    }))
                };
                const result = await httpPost(`api/${EntityNames.recalls}/saveAndProcessPicklist`, payload);
                if (result.status === HttpStatusCode.Ok) {
                    goToRecall();
                }
            }
        };

        const heading = shouldCancel ? "Warning" : "Move to pack?";
        const information = shouldCancel
            ? "You have cancelled all items, this will cancel the entire recall"
            : "";

        const modal = shouldCancel
            ? <CancelPicklistModal heading={heading} information={information} ContinueFunc={handle} />
            : <ConfirmModal heading={heading} information={information} ContinueFunc={handle} />;

        modalSignal.value = modal;
    };

    const cancel = () => {
        modalSignal.value = (
            <CancelPicklistModal
                heading="Cancel?"
                information="This action cannot be undone"
                ContinueFunc={handleCancel}
                type={ConfirmType.Destructive}
            />
        );
    };

    return {
        save,
        submit,
        cancel
    };
}